import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export function Card({
  title,
  hint,
  eyebrow,
  children,
  action,
  tone = 'default',
  interactive = false,
  className = '',
  style,
}: {
  title?: string;
  hint?: string;
  eyebrow?: string;
  children: ReactNode;
  action?: ReactNode;
  // 'hero' flips the card to a solid ink plate — reserve it for the one card
  // that carries the page. 'interactive' adds a hover lift for cards that are
  // also links.
  tone?: 'default' | 'hero';
  interactive?: boolean;
  // Passthrough for layout placement (grid col-spans, etc.) without leaking
  // the surface styling out of the component.
  className?: string;
  style?: CSSProperties;
}) {
  const hasHeader = Boolean(title || eyebrow || hint || action);
  const surface = tone === 'hero' ? 'card card-hero' : 'card';
  // No rounded-* here on purpose: the radius lives on `.card` so every surface
  // in the app is the same object.
  return (
    <section
      className={`${surface}${interactive ? ' card-hover' : ''} p-5 sm:p-6 space-y-3 ${className}`}
      style={style}
    >
      {hasHeader && (
        <header className="flex items-start justify-between gap-3">
          <div className="space-y-1 min-w-0">
            {eyebrow && <p className="label-eyebrow">{eyebrow}</p>}
            {title && (
              <h2 className="text-[17px] font-bold tracking-[-0.02em] text-fg">{title}</h2>
            )}
            {hint && <p className="text-xs text-fg-muted">{hint}</p>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
  disabled,
  hint,
  meta,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  hint?: string;
  // meta sits right-aligned next to the label (e.g. a scheduled time on a
  // meal row). Stays out of the hint line so secondary info doesn't get
  // mixed up with the meal's ingredient list.
  meta?: string;
}) {
  return (
    <label
      className={`flex items-start gap-3 p-2.5 rounded-panel select-none transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-fg/[0.04]'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="sr-only peer"
      />
      {/* Checked is solid ink with a white tick — the same black as every other
          committed action. Unchecked is an empty grey well. */}
      <span
        aria-hidden
        className={`mt-0.5 grid place-items-center size-5 rounded-[7px] border transition-all shrink-0 ${
          checked
            ? 'bg-accent border-accent text-accent-fg'
            : 'bg-bg-sunken border-line-strong text-transparent'
        }`}
      >
        <svg viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8.5l3.5 3.5L13 5" />
        </svg>
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <span className={`text-sm ${checked ? 'text-fg-muted line-through' : 'text-fg'}`}>{label}</span>
          {meta && (
            <span className="font-mono text-[11px] tabular-nums text-fg-faint shrink-0">
              {meta}
            </span>
          )}
        </div>
        {hint && <p className="text-xs text-fg-faint mt-0.5">{hint}</p>}
      </div>
    </label>
  );
}

export function Counter({
  label,
  unit,
  value,
  step = 1,
  min = 0,
  max = 99999,
  onChange,
}: {
  label: string;
  unit?: string;
  // null = never logged. It renders as an em-dash with no unit, the way every
  // other unlogged value on Today reads — a measurement nobody has taken is not
  // the number 0, and coercing it (waist has min 50) put a "0 cm" on screen and
  // made the first + tap look like a 50 cm jump.
  value: number | null;
  step?: number;
  min?: number;
  max?: number;
  onChange: (n: number) => void;
}) {
  // A tap counts up from the last number this control emitted, not from the
  // prop rendered on screen — two taps that land before the parent commits its
  // answer therefore still count as two steps instead of collapsing into one.
  // Re-anchored to `value` after every commit, so an external correction
  // (server truth, the paired Steps input, a day switch) always wins.
  const latest = useRef<number | null>(value);
  useEffect(() => {
    latest.current = value;
  });

  const bump = (dir: 1 | -1) => {
    const base = latest.current;
    // From unlogged, either button commits the floor: `min` is the smallest
    // number this control can honestly claim, and landing there from both
    // directions means a mis-tap doesn't pick a different starting point.
    // Otherwise clamp both ends in both directions — clamping only the
    // direction of travel stranded any value that starts outside the range.
    const next =
      base === null
        ? Math.min(max, min)
        : Math.min(max, Math.max(min, +(base + dir * step).toFixed(2)));
    latest.current = next;
    onChange(next);
  };

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-fg">{label}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => bump(-1)}
          aria-label={`Decrease ${label}`}
          className="size-9 rounded-full border border-line-strong text-fg-muted hover:text-fg hover:bg-bg-sunken text-lg leading-none transition-colors"
        >
          −
        </button>
        <span className="font-mono tabular-nums w-20 text-center text-fg text-base">
          {value === null ? '—' : value}
          {value !== null && unit ? <span className="text-fg-muted ml-1">{unit}</span> : null}
        </span>
        <button
          type="button"
          onClick={() => bump(1)}
          aria-label={`Increase ${label}`}
          className="size-9 rounded-full border border-line-strong text-fg-muted hover:text-fg hover:bg-bg-sunken text-lg leading-none transition-colors"
        >
          +
        </button>
      </div>
    </div>
  );
}

export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  hint,
}: {
  label: string;
  value: number | null;
  min: number;
  max: number;
  step?: number;
  onChange: (n: number) => void;
  hint?: string;
}) {
  const v = value ?? min;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-baseline">
        <span className="text-sm text-fg">{label}</span>
        <span className="font-mono text-sm tabular-nums text-fg">
          {value === null ? '—' : v}
        </span>
      </div>
      {/* accent-color is set globally to ink, so the filled track and thumb are
          black without per-browser pseudo-element styling. */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={v}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 cursor-pointer"
      />
      {hint && <p className="text-xs text-fg-faint">{hint}</p>}
    </div>
  );
}
